import type { Quiz } from "@peer-start/platform-contracts";
import { Button, Card } from "@peer-start/ui";
import { CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export function QuizPanel({
  quiz,
  controlId,
  pending,
  onSubmit,
}: {
  quiz: Quiz;
  controlId: string;
  pending: boolean;
  onSubmit: (answers: number[]) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Array<number | undefined>>(() =>
    quiz.questions.map(() => undefined),
  );
  const complete = answers.every((answer) => answer !== undefined);
  return (
    <Card>
      <div className="mb-6 flex items-center gap-3">
        <span className="grid size-11 place-items-center rounded-xl bg-accent text-accent-foreground">
          <CheckCircle2 aria-hidden="true" className="size-5" />
        </span>
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t("skill.questionProgress", { current: quiz.questions.length, total: quiz.questions.length })}
          </p>
          <h2 className="text-xl font-medium">{quiz.title}</h2>
        </div>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit(answers as number[]);
        }}
      >
        <div className="space-y-7">
          {quiz.questions.map((question, questionIndex) => (
            <fieldset key={question.id}>
              <legend className="mb-3 font-medium">{question.prompt}</legend>
              <div className="space-y-2">
                {question.options.map((option, optionIndex) => (
                  <label
                    className="flex cursor-pointer items-center gap-3 rounded-xl border border-border p-3 transition has-[:checked]:border-primary has-[:checked]:bg-muted"
                    key={option}
                  >
                    <input
                      checked={answers[questionIndex] === optionIndex}
                      data-control-id={`${controlId}.${question.id}.${optionIndex}`}
                      name={question.id}
                      onChange={() =>
                        setAnswers((current) =>
                          current.map((answer, index) => (index === questionIndex ? optionIndex : answer)),
                        )
                      }
                      type="radio"
                    />
                    {option}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
        <Button
          className="mt-7 w-full sm:w-auto"
          controlId={`${controlId}.submit`}
          disabled={!complete || pending}
          type="submit"
        >
          {t("skill.submitAnswers")}
        </Button>
      </form>
    </Card>
  );
}
